import { Button, ButtonGroup, Divider, Label, Radio, RadioGroup } from "@blueprintjs/core";
import classNames from "classnames";
import React, { FormEvent, useCallback } from "react";

import { Accordion, AccordionItemProps } from "../../../../../components";
import Checkbox from "../../../../../components/CheckBox/Checkbox";
import { useAppSelector } from "../../../../../redux/hooks";
import { CourtOption, Sport } from "../../../../../redux/slices";
import { DrawingArea, FrameStencil } from "../../../../../types";
import { getPlayingSurfaceLabelFromSport } from "../../../../../utils";
import { usePlayContext } from "../../../play-context";
import { StencilTools } from "../stencil-tools/stencil-tools";
import styles from "./court-settings.module.scss";

interface HandleCourtSettingChangeProps {
  courtContentOption: CourtOption["id"];
  checked: boolean;
}

interface HandleOutOfBoundsChangeProps {
  outOfBoundsOption: "all" | "End" | "Left" | "Right";
  checked: boolean;
}

interface CourtSettingsProps {
  stencil: FrameStencil | undefined;
}

export const CourtSettings = ({ stencil }: CourtSettingsProps) => {
  const { courtViews, courtOptions } = useAppSelector((state) => state.sport);

  const { playStateApi } = usePlayContext();
  const { updatePartialPlay, playState } = playStateApi;
  const { sport } = playState;

  const playingSurfaceLabel = getPlayingSurfaceLabelFromSport(sport);

  const handleChangeForDrawingArea = (e: FormEvent<HTMLInputElement>) => {
    const drawingArea = e.currentTarget.value as DrawingArea;
    updatePartialPlay({ playSurfaceView: drawingArea });
  };

  const handleCourtSettingChange = ({ courtContentOption, checked }: HandleCourtSettingChangeProps) => {
    updatePartialPlay({ courtOptions: { ...playState.courtOptions, [courtContentOption]: checked } });
  };

  const handleOutOfBoundsChange = ({ outOfBoundsOption, checked }: HandleOutOfBoundsChangeProps) => {
    updatePartialPlay({ properties: { ...playState.properties, [`outOfBounds${outOfBoundsOption}`]: checked } });
  };

  const toggleSoccerOutOfBounds = useCallback(() => {
    if (sport === Sport.SOCCER) {
      const outOfBounds = playState.outOfBounds;
      updatePartialPlay({ outOfBounds: !outOfBounds });
    }
  }, [sport, playState, updatePartialPlay]);

  const currentPlayRotation = playState.orientation;

  const rotateLeft = useCallback(() => {
    const newOrientation = currentPlayRotation === 0 ? 270 : ((currentPlayRotation - 90) as 0 | 90 | 180 | 270);
    updatePartialPlay({ orientation: newOrientation });
  }, [currentPlayRotation, updatePartialPlay]);

  const rotateRight = useCallback(() => {
    const newOrientation = currentPlayRotation === 270 ? 0 : ((currentPlayRotation + 90) as 0 | 90 | 180 | 270);
    updatePartialPlay({ orientation: newOrientation });
  }, [currentPlayRotation, updatePartialPlay]);

  const colorPickerAccordionItem: AccordionItemProps[] = stencil
    ? [
        {
          type: "STANDARD",
          id: "color-picker",
          label: "Color Picker",
          bodyRender: <StencilTools stencil={stencil} />,
          isOpen: true,
        },
      ]
    : [];

  const outOfBoundsAccordionItem: AccordionItemProps[] = ((): AccordionItemProps[] => {
    switch (sport) {
      case Sport.BASKETBALL:
        return [
          {
            type: "STANDARD",
            id: "out-of-bounds",
            label: "Out of Bounds",
            bodyRender: (
              <div className={classNames(styles.inputGroup)}>
                <Checkbox
                  label="End"
                  checked={playState.properties.outOfBoundsEnd || false}
                  className={styles.courtInput}
                  onChange={(e) =>
                    handleOutOfBoundsChange({ outOfBoundsOption: "End", checked: e.currentTarget.checked })
                  }
                />
                <Checkbox
                  label="Left"
                  checked={playState.properties.outOfBoundsLeft || false}
                  className={styles.courtInput}
                  onChange={(e) =>
                    handleOutOfBoundsChange({ outOfBoundsOption: "Left", checked: e.currentTarget.checked })
                  }
                />
                <Checkbox
                  label="Right"
                  checked={playState.properties.outOfBoundsRight || false}
                  className={styles.courtInput}
                  onChange={(e) =>
                    handleOutOfBoundsChange({ outOfBoundsOption: "Right", checked: e.currentTarget.checked })
                  }
                />
              </div>
            ),
          },
        ];

      case Sport.HOCKEY:
        return [];

      case Sport.SOCCER:
        return [
          {
            type: "STANDARD",
            id: "out-of-bounds",
            label: "Out of Bounds",
            bodyRender: (
              <div className={classNames(styles.inputGroup)}>
                <Checkbox
                  label="Out of Bounds"
                  checked={playState.outOfBounds}
                  className={styles.courtInput}
                  onChange={toggleSoccerOutOfBounds}
                />
              </div>
            ),
          },
        ];
    }
  })();

  const accordionItems: AccordionItemProps[] = [
    ...colorPickerAccordionItem,
    {
      type: "STANDARD",
      id: "drawing-area",
      label: `${playingSurfaceLabel} Dimensions`,
      bodyRender: (
        <>
          <RadioGroup
            selectedValue={playState.playSurfaceView}
            onChange={handleChangeForDrawingArea}
            className={classNames(styles.inputGroup)}
          >
            {courtViews &&
              courtViews.map((courtView, i) => {
                return (
                  <Radio
                    key={i}
                    label={courtView.label}
                    value={courtView.id}
                    className={classNames(styles.radioButton, styles.courtInput)}
                  />
                );
              })}
          </RadioGroup>
          <Divider />
          <Label className={styles.rotateLabel}>Rotation:</Label>
          <ButtonGroup fill>
            <Button icon="image-rotate-left" onClick={rotateLeft} />
            <Button icon="image-rotate-right" onClick={rotateRight} />
          </ButtonGroup>
        </>
      ),
    },
    ...outOfBoundsAccordionItem,
    {
      type: "STANDARD",
      id: "court-content",
      label: `${playingSurfaceLabel} Content`,
      bodyRender: (
        <div className={classNames(styles.inputGroup)}>
          {courtOptions &&
            courtOptions.map((option, i) => (
              <Checkbox
                key={i}
                className={styles.courtInput}
                checked={playState.courtOptions[option.id] == null ? true : playState.courtOptions[option.id]}
                label={option.label}
                onChange={(event) =>
                  handleCourtSettingChange({ courtContentOption: option.id, checked: event.currentTarget.checked })
                }
              />
            ))}
        </div>
      ),
    },
  ];

  return <Accordion items={accordionItems} />;
};
